<template>
  <div ref="onBoardingTextToolRef" class="text-tool-component">
    <ToolTitle :title="title" />
    <div id="text-wrap">
      <textarea
        @input="setTextProp($event, 'text')"
        id="text-editor"
        name="text-editor"
        v-model="currentText"
      ></textarea>
      <div id="selectors-wrap">
        <div
          id="font-selector"
          :class="showFonts ? ' bottom-border' : ''"
          @click="showFonts = !showFonts"
        >
          <div class="flex-container">
            <div class="currently-selected">
              <span>{{ currentFont }}</span>
            </div>
            <img src="@/assets/caret.svg" :class="showFonts ? 'animCaretDown' : 'animCaretUp'" />
          </div>
          <transition name="expand" mode="out-in">
            <div v-if="showFonts" class="dropDownFont dropDownScroll">
              <div
                v-for="(font, idx) in filteredFonts"
                :key="idx"
                :value="font"
                class="listName"
                name="font"
                @click="setTextProp($event, 'fontFamily', font)"
              >
                <span :style="{ fontFamily: font }">
                  {{ font }}
                </span>
              </div>
            </div>
          </transition>
        </div>
        <div
          id="size-selector"
          :class="showSizes ? ' bottom-border' : ''"
          @click="showSizes = !showSizes"
        >
          <div class="flex-container">
            <div class="currently-selected">
              <input
                type="text"
                @input="handleFontSize"
                :value="Math.round(pxToPts(currentFontSize) * 10) / 10"
              />
            </div>
            <img src="@/assets/caret.svg" :class="showSizes ? 'animCaretDown' : 'animCaretUp'" />
          </div>
          <transition name="expand" mode="out-in">
            <div v-if="showSizes" class="dropDownSize dropDownScroll">
              <div
                v-for="(size, idx) in filteredFontSizes"
                :key="idx"
                class="listName"
                name="size"
                @click="setTextProp($event, 'fontSize', size)"
              >
                <span>
                  {{ Math.round(pxToPts(size) * 10) / 10 }}
                </span>
              </div>
            </div>
          </transition>
        </div>
      </div>
      <div id="style-alignment-options">
        <div id="styles">
          <div :style="{ opacity: !hasBold ? '.4' : '' }">
            <input
              @click="setTextProp($event, 'fontWeight')"
              name="fontWeight"
              id="fontWeight"
              type="checkbox"
              :disabled="!hasBold"
            />
            <label for="fontWeight">
              <font-awesome-icon class="" icon="bold" aria-hidden="true" />
            </label>
          </div>
          <div :style="{ opacity: !hasItalic ? '.4' : '' }">
            <input
              @click="setTextProp($event, 'fontStyle')"
              name="fontStyle"
              id="fontStyle"
              type="checkbox"
              :disabled="!hasItalic"
            />
            <label for="fontStyle">
              <font-awesome-icon class="" icon="italic" aria-hidden="true" />
            </label>
          </div>
          <div>
            <input
              @click="setTextProp($event, 'underline')"
              name="underline"
              id="underline"
              type="checkbox"
            />
            <label for="underline">
              <font-awesome-icon class="" icon="underline" aria-hidden="true" />
            </label>
          </div>
        </div>
        <div id="alignments">
          <div>
            <input
              @click="setTextProp($event, 'textAlign', 'left')"
              name="textAlign"
              id="leftAlign"
              type="radio"
              checked
            />
            <label for="leftAlign">
              <font-awesome-icon class="" icon="align-left" aria-hidden="true" />
            </label>
          </div>
          <div>
            <input
              @click="setTextProp($event, 'textAlign', 'center')"
              name="textAlign"
              id="centerAlign"
              type="radio"
            />
            <label for="centerAlign">
              <font-awesome-icon class="" icon="align-center" aria-hidden="true"
            /></label>
          </div>
          <div>
            <input
              @click="setTextProp($event, 'textAlign', 'right')"
              name="textAlign"
              id="rightAlign"
              type="radio"
            />
            <label for="rightAlign">
              <font-awesome-icon class="" icon="align-right" aria-hidden="true" />
            </label>
          </div>
        </div>
      </div>
      <div id="color-inputs">
        <div class="color-input-container">
          <p>Fill</p>
          <div id="fill-circle" ref="fillElement" @click="handleToggleColorPickr"></div>
        </div>
        <div class="color-input-container">
          <p>Border</p>
          <div id="border-circle" ref="borderElement" @click="handleToggleColorPickr"></div>
        </div>
      </div>
      <div id="range-inputs">
        <div class="range-wrap">
          <p>Border Width</p>
          <input
            type="range"
            min="0"
            max="100"
            class="slider"
            :value="strokeWidth"
            ref="strokeWidthRange"
            @input="setTextProp($event, 'strokeWidth')"
          />
          <div>{{ strokeWidth / 10 }}px</div>
        </div>
        <div class="range-wrap">
          <p>Transparency</p>
          <input
            type="range"
            min="0"
            max="100"
            :value="opacity"
            class="slider"
            ref="opacityRange"
            @input="setTextProp($event, 'opacity')"
          />
          <div>{{ opacity }}%</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted, nextTick } from "vue";
import { fabric } from "fabric";
import { store } from "@/store";
import ToolTitle from "@/components/SideToolBar/Tools/ToolTitle.vue";
import {
  currentText,
  currentFont,
  currentFontSize,
  makeNewText,
  updateText,
  pxToPts,
  ptsToPx,
} from "@/canvas-api/UserObjects/Text";
import usePickr from "@/composables/usePickr";
import { ColorPickrState } from "@/types";
import fontData from "@/data/fonts.json";
import { onBoardingTextToolRef } from "@/onboarding/Refs";
import { opacity, strokeWidth, fillElement, borderElement } from "@/canvas-api/EventListeners"

// prettier-ignore
const fontSizesData = [6.667, 7.333, 8.667, 10, 10.667, 12, 13.333, 14, 14.667, 16, 18.667, 21.333, 24, 26.667, 29.333, 32, 34.667, 37.333, 48, 64, 96];

export default defineComponent({
  name: "TexTool",
  components: {
    ToolTitle,
  },
  setup() {
    const title = ref("");

    const showFonts = ref(false);
    const formattedFonts = ref<string[]>(Object.values(fontData).map((font) => font.split(":")[0]));
    const filteredFonts = computed(() =>
      formattedFonts.value.filter((font) => font !== currentFont.value)
    );

    const showSizes = ref(false);
    const fontSizes = ref<number[]>(fontSizesData);
    const filteredFontSizes = computed(() =>
      fontSizes.value.filter((size) => size !== currentFontSize.value)
    );

    const handleFontSize = (e: { target: HTMLInputElement }) => {
      const value = Number(e.target.value);

      if (!Number.isNaN(value)) {
        currentFontSize.value = ptsToPx(value);

        updateText("fontSize", currentFontSize.value);
      }
    };


    const currentActiveObject = computed(() => store.state.currentActiveObject as fabric.IText);
    const colorPickrState = computed<ColorPickrState>(() => store.getters.colorPickrState);

    const hasBold = ref(false);
    const hasItalic = ref(false);
    const hasBoldItalic = ref(false);
    const checkFontAttrs = async (fonts: any, currentlySelectedFont: string) => {
      hasBold.value = false;
      hasItalic.value = false;
      hasBoldItalic.value = false;

      const fontValue = fonts[currentlySelectedFont] ? fonts[currentlySelectedFont].split(":") : [];

      if (!fontValue[1]) {
        let weightInput = document.getElementById("fontWeight") as HTMLInputElement;
        weightInput.checked = false;

        let styleInput = document.getElementById("fontStyle") as HTMLInputElement;
        styleInput.checked = false;

        //Throw in some await nextTicks because if we switch to a fontFamily that does not have
        //Bold or Italic and we overload the watcher with too many changes at once
        //The watcher in Canvas.vue only sees one change
        await nextTick();
        updateText("fontWeight", "normal");
        await nextTick();
        updateText("fontStyle", "normal");
        return;
      }

      const fontAttrs = fontValue[1].split(",");

      fontAttrs.forEach((attr: string) => {
        if (attr.includes("700")) {
          hasBold.value = true;
        }

        if (attr.includes("400i")) {
          hasItalic.value = true;
        }

        if (attr.includes("700i")) {
          hasBoldItalic.value = true;
        }
      });

      if (!hasBold.value) {
        let weightInput = document.getElementById("fontWeight") as HTMLInputElement;
        weightInput.checked = false;

        await nextTick();
        updateText("fontWeight", "normal");
      }

      if (!hasItalic.value) {
        let styleInput = document.getElementById("fontStyle") as HTMLInputElement;
        styleInput.checked = false;

        await nextTick();
        updateText("fontStyle", "normal");
      }
    };

    onMounted(() => {
      const defaultSize = 24;

      if (!currentActiveObject.value || currentActiveObject.value.name !== "iText") {
        const defaultText = "Enter Text Here";
        const defaultFont = "Open Sans";

        currentText.value = defaultText;
        currentFont.value = defaultFont;
        currentFontSize.value = defaultSize;

        makeNewText(currentText.value, currentFontSize.value, currentFont.value);

        if (borderElement.value) {
          borderElement.value.style.background = "#0001ba";
        }

        if (fillElement.value) {
          fillElement.value.style.background = "#000";
        }

        opacity.value = 0;
        strokeWidth.value = 0
      } else if (currentActiveObject.value.name === "iText") {
        let activeObj = currentActiveObject.value as fabric.IText;

        currentText.value = activeObj.text!;
        currentFont.value = activeObj.fontFamily!;
        currentFontSize.value = activeObj.fontSize! * activeObj.scaleX!;

        if (activeObj.fontWeight === "bold") {
          let input = document.getElementById("fontWeight") as HTMLInputElement;
          input.checked = true;
        }

        if (activeObj.fontStyle === "italic") {
          let input = document.getElementById("fontStyle") as HTMLInputElement;
          input.checked = true;
        }

        if (activeObj.underline) {
          let input = document.getElementById("underline") as HTMLInputElement;
          input.checked = true;
        }

        let textAlignInput = document.getElementById(
          `${activeObj.textAlign}Align`
        ) as HTMLInputElement;

        if (textAlignInput) {
          textAlignInput.checked = true;
        }

        if (borderElement.value) {
          borderElement.value.style.background = activeObj.stroke as string;
        }

        if (fillElement.value) {
          fillElement.value.style.background = activeObj.fill as string;
        }

        opacity.value = 100 - activeObj.opacity! * 100;
        strokeWidth.value = activeObj.strokeWidth! * 10;
      }

      checkFontAttrs(fontData, currentFont.value);

      //Triggering rerender on mount, if no rerender trigger, canvas doesn"t get new Itext Object
      title.value = "Text";
    });

    const setTextProp = (
      e: { target: HTMLInputElement },
      prop: string,
      value?: string | number
    ) => {
      let checked = e.target.checked;

      switch (prop) {
        case "fontFamily": {
          currentFont.value = value as string;
          updateText(prop, currentFont.value);
          checkFontAttrs(fontData, currentFont.value);
          break;
        }
        case "fontSize": {
          currentFontSize.value = value as number;

          updateText(prop, currentFontSize.value);
          break;
        }
        case "fontWeight": {
          if (checked) updateText(prop, "bold");
          else updateText(prop, "normal");

          break;
        }
        case "fontStyle": {
          if (checked) updateText(prop, "italic");
          else updateText(prop, "normal");

          break;
        }
        case "underline": {
          if (checked) updateText(prop, true);
          else updateText(prop, false);

          break;
        }
        case "textAlign": {
          updateText(prop, value as string);

          break;
        }
        case "strokeWidth": {
          let inputValue = Number(e.target.value);

          strokeWidth.value = inputValue;

          updateText(prop, strokeWidth.value / 10);
          break;
        }
        case "opacity": {
          let inputValue = Number(e.target.value);

          opacity.value = inputValue;

          updateText(prop, 1 - opacity.value / 100);
          break;
        }
        case "text": {
          updateText(prop, currentText.value);

          break;
        }
      }
    };

    const handleToggleColorPickr = (event: { target: HTMLDivElement }): void => {
      const TARGET = event.target;

      switch (TARGET.getAttribute("id")) {
        case "fill-circle": {
          let payload = {
            ...colorPickrState.value,
            isOpen: false,
            id: TARGET.getAttribute("id")!,
            property: {
              name: "fill",
              value: TARGET.style.backgroundColor,
            },
          };

          usePickr(payload);
          break;
        }
        case "border-circle": {
          let payload = {
            ...colorPickrState.value,
            isOpen: false,
            id: TARGET.getAttribute("id")!,
            property: {
              name: "stroke",
              value: TARGET.style.backgroundColor,
            },
          };

          usePickr(payload);
          break;
        }
        default: {
          throw new Error("Missing target id!, Customize.vue");
        }
      }
    };

    return {
      onBoardingTextToolRef,
      pxToPts,
      handleFontSize,
      currentText,
      currentFont,
      currentFontSize,
      title,
      showFonts,
      formattedFonts,
      filteredFonts,
      showSizes,
      fontSizes,
      filteredFontSizes,
      // fontSizeInput,
      fillElement,
      borderElement,
      opacity,
      strokeWidth,
      hasBold,
      hasItalic,
      hasBoldItalic,
      setTextProp,
      handleToggleColorPickr,
    };
  },
});
</script>

<style>
#text-wrap {
  width: 100%;
  height: calc(100% - 75px);
  margin-top: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

#text-wrap textarea {
  width: 300px;
  height: 100px;
  border-radius: 4px;
  font-size: 16px;
  padding: 6.5px;
}

#selectors-wrap {
  width: 315px;
  display: flex;
  justify-content: space-between;
  margin: 20px 0;
}

#font-selector {
  width: 60%;
}

#size-selector {
  width: 30%;
}

#font-selector,
#size-selector {
  height: 30px;
  background-color: white;
  transition: border-radius linear 0.25s;
  border-radius: 4px;
}

.flex-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 100%;
}

.bottom-border {
  border-bottom-left-radius: 0px !important;
  border-bottom-right-radius: 0px !important;
  padding: 0px;
  transition: border-radius linear 0.25s;
}

.currently-selected {
  padding-left: 7px;
  color: black;
  font-size: 16px;
}

.currently-selected input,
.currently-selected input:focus {
  border: none;
  outline: none;
  width: 50%;
}

.animCaretUp {
  height: 20%;
  padding-right: 7px;
  transform: rotate(0deg);
  transition-duration: 0.25s;
}

.animCaretDown {
  height: 20%;
  padding-left: 7px;
  transform: rotate(180deg) scale(1.2, 1.2);
  transition-duration: 0.25s;
}

.dropDownFont,
.dropDownSize {
  width: 100%;
  height: 200px;
  overflow-y: scroll;
  position: relative;
  background-color: #fdfdfd;
  border: 1px solid #c7c4ca;
  border-top: 0px;
  z-index: 5;
  border-bottom-left-radius: 10px;
  border-bottom-right-radius: 10px;
  color: black;
  font-size: 16px;
}

.dropDownScroll::-webkit-scrollbar {
  width: 10px;
}

.dropDownScroll::-webkit-scrollbar-thumb {
  background: #666;
  border-radius: 20px;
}

.dropDownScroll::-webkit-scrollbar-track {
  background: #ddd;
  border-radius: 20px;
}

.listName {
  border: 2px solid transparent;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding: 5px;
}

.listName:hover {
  border: 2px solid rgb(0, 174, 255);
  padding: 5px;
}

.listName:last-child:hover {
  border: 2px solid rgb(0, 174, 255);
  padding: 5px;
  border-bottom-left-radius: 10px;
}

.expand-enter-active,
.expand-leave-active {
  max-height: 200px;
  transition: max-height 0.25s ease-out;
}

.expand-enter-from,
.expand-leave-to {
  max-height: 0px;
  transition: max-height 0.25s ease-out;
}

#style-alignment-options {
  width: 90%;
  height: 30px;
  margin: 0 auto 20px auto;
  display: flex;
  justify-content: space-between;
}

#style-alignment-options input {
  display: none;
}

#styles,
#alignments {
  height: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

#styles div,
#alignments div {
  width: 30px;
  height: 30px;
}

#styles label,
#alignments label {
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
}

#styles span,
#alignments span {
  font-size: 14px;
}

#styles div input[type="checkbox"]:checked + label,
#alignments div input[type="radio"]:checked + label {
  background: #838591;
}

#color-inputs,
#range-inputs {
  width: 315px;
  margin: auto;
}

#color-inputs {
  display: flex;
  justify-content: space-evenly;
  margin-bottom: 20px;
}

.color-input-container {
  width: 75px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  /* align-items: center; */
}

#color-inputs p {
  font-weight: 400;
  font-size: 16px;
  margin-left: 12.5px;
}

#fill-circle,
#border-circle {
  width: 50px;
  height: 50px;
  background: #e6e6e6;
  border: 3px solid white;
  border-radius: 50%;
  margin: auto;
}

#border-circle {
  background: #0001ba;
}

.range-wrap {
  width: 100%;
  height: 90px;
  display: flex;
  flex-direction: column;
  justify-content: space-evenly;
}

.range-wrap p {
  color: #dcdcdc;
  font-weight: 400;
  font-size: 16px;
  padding-bottom: 2px;
}

.range-wrap input {
  width: 100%;
  background: 0 0;
}

.range-wrap div {
  margin: 0 auto;
}

@media (max-width: 767px) {
  #text-editor {
    max-width: 400px;
    width: 90% !important;
  }

  .currently-selected input,
  .currently-selected span {
    width: 25%;
  }

  #selectors-wrap,
  #style-alignment-options,
  #color-inputs,
  #range-inputs {
    max-width: 415px;
    width: calc(90% + 15px);
  }
}
</style>
